import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, ArrowRight } from "lucide-react";
import { site } from "@/data/site";
import { Reveal } from "./Reveal";

const inputClass =
    "w-full border border-line bg-ivory px-4 py-3.5 text-sm text-charcoal outline-none transition-colors duration-300 placeholder:text-slatey/70 focus:border-orange";
const labelClass =
    "mb-2 block text-[10px] uppercase tracking-[0.18em] text-slatey";

export const Consultation = () => {
    const empty = {
        name: "",
        phone: "",
        email: "",
        budget: "",
        project: "",
        date: "",
        message: "",
    };
    const [form, setForm] = useState(empty);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim()) {
            toast.error("Please share your name and phone number.");
            return;
        }
        const lines = [
            "New Private Consultation Request — Swastik Realty",
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            form.email && `Email: ${form.email}`,
            form.budget && `Budget: ${form.budget}`,
            form.project && `Preferred Project: ${form.project}`,
            form.date && `Preferred Visit Date: ${form.date}`,
            form.message && `Message: ${form.message}`,
        ].filter(Boolean);
        // Persist enquiry locally for future CRM integration/export.
        try {
            const key = "swastik_enquiries";
            const prev = JSON.parse(localStorage.getItem(key) || "[]");
            prev.push({ ...form, createdAt: new Date().toISOString() });
            localStorage.setItem(key, JSON.stringify(prev));
        } catch (_) {}

        const url = `https://wa.me/${site.contact.whatsapp}?text=${encodeURIComponent(
            lines.join("\n"),
        )}`;
        window.open(url, "_blank", "noopener,noreferrer");
        toast.success("Consultation request prepared — we'll be in touch shortly.");
        setForm(empty);
    };

    return (
        <section
            id="consultation"
            data-testid="consultation-section"
            className="bg-ivory px-6 py-16 lg:px-10 lg:py-24"
        >
            <div className="mx-auto grid max-w-[1400px] gap-12 lg:grid-cols-2 lg:gap-20">
                <Reveal>
                    <div>
                        <span className="eyebrow">Private Consultation</span>
                        <h2 className="mt-5 font-serif text-4xl leading-[1.05] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
                            Book Your Private
                            <span className="italic text-orange"> Consultation.</span>
                        </h2>
                        <p className="mt-7 max-w-md text-base leading-relaxed text-slatey">
                            Share a few details and one of our advisors will curate a
                            private shortlist and arrange discreet viewings at your
                            convenience.
                        </p>

                        <div className="mt-10 space-y-4">
                            <a
                                href={`tel:${site.contact.phone}`}
                                data-testid="consultation-call"
                                className="group flex items-center gap-4 text-charcoal"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-line text-orange transition-colors duration-300 group-hover:bg-orange group-hover:text-white">
                                    <Phone size={18} />
                                </span>
                                <span className="text-sm">{site.contact.phoneDisplay}</span>
                            </a>
                            <a
                                href={`mailto:${site.contact.email}`}
                                data-testid="consultation-email"
                                className="group flex items-center gap-4 text-charcoal"
                            >
                                <span className="flex h-11 w-11 items-center justify-center border border-line text-orange transition-colors duration-300 group-hover:bg-orange group-hover:text-white">
                                    <Mail size={18} />
                                </span>
                                <span className="text-sm">{site.contact.email}</span>
                            </a>
                        </div>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <form
                        data-testid="consultation-form"
                        onSubmit={submit}
                        className="border border-line bg-white p-7 lg:p-10"
                    >
                        <div className="grid gap-5 sm:grid-cols-2">
                            <div>
                                <label className={labelClass}>Name</label>
                                <input
                                    data-testid="form-name"
                                    className={inputClass}
                                    value={form.name}
                                    onChange={set("name")}
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Phone</label>
                                <input
                                    data-testid="form-phone"
                                    className={inputClass}
                                    value={form.phone}
                                    onChange={set("phone")}
                                    placeholder="+91"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Email</label>
                                <input
                                    data-testid="form-email"
                                    type="email"
                                    className={inputClass}
                                    value={form.email}
                                    onChange={set("email")}
                                    placeholder="you@email.com"
                                />
                            </div>
                            <div>
                                <label className={labelClass}>Budget</label>
                                <select
                                    data-testid="form-budget"
                                    className={inputClass}
                                    value={form.budget}
                                    onChange={set("budget")}
                                >
                                    <option value="">Select budget</option>
                                    {site.budgets.map((b) => (
                                        <option key={b} value={b}>
                                            {b}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>Preferred Project</label>
                                <select
                                    data-testid="form-project"
                                    className={inputClass}
                                    value={form.project}
                                    onChange={set("project")}
                                >
                                    <option value="">No preference</option>
                                    {site.projects.map((p) => (
                                        <option key={p.id} value={p.name}>
                                            {p.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass}>
                                    Preferred Visit Date
                                </label>
                                <input
                                    data-testid="form-date"
                                    type="date"
                                    className={inputClass}
                                    value={form.date}
                                    onChange={set("date")}
                                />
                            </div>
                        </div>

                        <div className="mt-5">
                            <label className={labelClass}>Message</label>
                            <textarea
                                data-testid="form-message"
                                rows={4}
                                className={`${inputClass} resize-none`}
                                value={form.message}
                                onChange={set("message")}
                                placeholder="Tell us what you're looking for…"
                            />
                        </div>

                        <button
                            data-testid="form-submit"
                            type="submit"
                            className="group mt-7 flex w-full items-center justify-center gap-3 bg-orange px-8 py-4 text-xs uppercase tracking-[0.15em] text-white transition-colors duration-300 hover:bg-orange-dark"
                        >
                            Schedule My Consultation
                            <ArrowRight
                                size={15}
                                className="transition-transform duration-300 group-hover:translate-x-1"
                            />
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
